<template>
  <div
    class="flex flex-col justify-center mb-2 bg-white rounded-xl border border-gray-300 border-solid"
  >
    <div class="flex flex-col justify-center w-full rounded-xl">
      <div
        class="flex gap-10 justify-between items-center px-6 py-5 rounded-xl w-full bg-slate-50 cursor-pointer"
        @click="toggleCollapse"
      >
        <div class="flex items-center gap-2">
          <div
            class="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-black font-semibold text-sm"
          >
            3
          </div>
          <h3 class="text-sm font-semibold text-black">Title Text</h3>
        </div>

        <div class="flex items-center gap-4 py-2 px-2">
          <button
            class="flex items-center w-12 h-6 rounded-xl transition-colors duration-300"
            :class="{
              'bg-violet-600': isVisible,
              'bg-gray-200': !isVisible,
            }"
            @click="toggleVisibilityHeader()"
            aria-label="Toggle button"
          >
            <span
              class="flex self-stretch my-auto w-5 h-5 bg-white rounded-full transition-transform duration-300 transform"
              :class="{
                'translate-x-6': isVisible,
                'translate-x-0.5': !isVisible,
              }"
            ></span>
          </button>
          <!-- Button for toggle top header bar -->
          <button class="w-5" aria-label="Toggle top header bar">
            <div v-html="isCollapsed ? ARROW_DOWN : ARROW_UP"></div>
          </button>
        </div>
      </div>

      <transition @before-enter="beforeEnter" @enter="enter" @leave="leave">
        <div
          class="flex flex-col w-full px-6 pb-5 rounded-xl max-md:px-5"
          v-if="!isCollapsed"
          ref="collapsible"
        >
          <div class="flex flex-col mb-4 mt-4 text-left pb-4">
            <div class="flex flex-col mb-2">
              <BaseLabel
                forAttr="headlineText"
                text="Headline Text"
                class="font-inter text-sm font-medium leading-normal tracking-normal"
              />
              <textarea
                v-model="localHeadlineText"
                @input="handleHeadlineTextChange($event)"
                placeholder="Type your text here..."
                class="w-full h-12 border rounded-md p-2 resize-none focus:outline-none"
                :class="{
                  'border-red-500': headlineTextWarning,
                  'border-gray-300': !headlineTextWarning,
                }"
                rows="2"
              ></textarea>
              <span
                class="text-xs mt-1 text-gray-400"
                :class="{ 'text-red-500': headlineTextWarning }"
              >
                {{ localHeadlineText.length }} / 50
              </span>
            </div>

            <div class="flex flex-col mb-1">
              <BaseLabel
                forAttr="paragraphText"
                text="Paragraph Text"
                class="font-inter text-sm font-medium leading-normal tracking-normal"
              />
              <BaseInput
                id="paragraphText"
                v-model="localParagraphText"
                type="text"
                @input="handleParagraphTextChange($event)"
                placeholder="Type your text here..."
                :className="{
                  'flex-grow px-3.5 py-2.5 mt-1 text-sm leading-5 text-gray-900 whitespace-nowrap bg-white rounded-md border border-gray-300 border-solid shadow-sm max-md:pr-5 w-full focus:outline-none': true,
                  'border-red-500': paragraphTextWarning,
                  'border-gray-300': !paragraphTextWarning,
                }"
              />
              <span
                class="text-xs mt-1 text-gray-400"
                :class="{ 'text-red-500': paragraphTextWarning }"
              >
                {{ localParagraphText.length }} / 100
              </span>
            </div>

            <div class="flex flex-col mt-1">
              <BaseLabel
                forAttr="selectedFontFamily"
                text="Font"
                class="font-inter text-sm font-medium leading-normal tracking-normal"
              />
              <select
                v-model="selectedFontFamily"
                @change="applyFontFamily($event)"
                class="border rounded px-2 py-1"
              >
                <option value="">Select Font</option>
                <option v-for="font in fonts" :key="font" :value="font">
                  {{ font }}
                </option>
              </select>
            </div>

            <div class="flex flex-row mt-3 gap-2">
              <select
                v-model="selectedBoldness"
                @change="applyBoldness($event)"
                class="border rounded py-1 w-1/2"
              >
                <option value="">Select Boldness</option>
                <option
                  v-for="(weight, index) in fontWeights"
                  :key="index"
                  :value="weight.value"
                >
                  {{ weight.label }}
                </option>
              </select>
            </div>

            <div class="flex flex-col mb-1">
              <BaseLabel
                forAttr="headlineFontSize"
                text="Headline Font Size"
                class="font-inter text-sm font-medium leading-normal tracking-normal mt-3"
              />

              <select
                v-model="selectedHeaderFontSize"
                @change="applyFontSize($event, 'header')"
                class="border rounded py-1 w-1/2"
              >
                <option value="">Select Font Size</option>
                <option v-for="size in fontSizes" :key="size" :value="size">
                  {{ size }}
                </option>
              </select>
            </div>

            <div class="flex flex-col mb-1">
              <BaseLabel
                forAttr="paragraphFontSize"
                text="Paragraph Font Size"
                class="font-inter text-sm font-medium leading-normal tracking-normal mt-3"
              />

              <select
                v-model="selectedParagraphFontSize"
                @change="applyFontSize($event, 'paragraph')"
                class="border rounded py-1 w-1/2"
              >
                <option value="">Select Font Size</option>
                <option v-for="size in fontSizes" :key="size" :value="size">
                  {{ size }}
                </option>
              </select>
            </div>

            <div class="flex flex-col mb-1">
              <BaseLabel
                forAttr="textColor"
                text="Text Color"
                class="font-inter text-sm font-medium leading-normal tracking-normal mt-3"
              />

              <div
                class="flex w-32 mt-1 items-center border border-gray-300 bg-white overflow-hidden rounded px-2 py-1"
              >
                <input
                  v-model="textColor"
                  type="color"
                  @input="applyTextColor($event)"
                  class="w-7 h-7 shrink-0"
                />
                <input
                  v-model="textColor"
                  @input="applyTextColor($event)"
                  class="ml-2 bg-transparent border-none focus:outline-none text-sm text-gray-800"
                />
              </div>
            </div>

            <div class="flex flex-col mb-1">
              <BaseLabel
                forAttr="backgroundColor"
                text="Background Color"
                class="font-inter text-sm font-medium leading-normal tracking-normal mt-3"
              />

              <div
                class="flex w-32 mt-1 items-center border border-gray-300 bg-white overflow-hidden rounded px-2 py-1"
              >
                <input
                  v-model="selectedBackgroundColor"
                  type="color"
                  @input="applyBackgroundColor($event)"
                  class="w-7 h-7 shrink-0"
                />
                <input
                  v-model="selectedBackgroundColor"
                  @input="applyBackgroundColor($event)"
                  class="ml-2 bg-transparent border-none focus:outline-none text-sm text-gray-800"
                />
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, Ref, ref, watch } from "vue";
import { useEditor } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import TextStyle from "@tiptap/extension-text-style";
import BaseLabel from "@/components/shared/BaseLabel.vue";
import BaseInput from "@/components/shared/BaseInput.vue";
import { ARROW_DOWN, ARROW_UP } from "@/assets/svg/shared/svg-constants";
import { useTiptapMarks } from "@/composables/useTiptapMarks";
import { useStylingSettings } from "@/composables/useStylingSettings";

const { Boldness, FontFamily, FontSize, TextColor, BackgroundColor } =
  useTiptapMarks();

export default defineComponent({
  name: "DirectoryHeaderDesign",
  components: {
    BaseLabel,
    BaseInput,
  },
  props: {
    fontFamily: {
      type: String,
      default: "",
    },
    boldness: {
      type: String,
      default: "",
    },
    headerTextColor: {
      type: String,
      default: "",
    },
    backgroundColor: {
      type: String,
      default: "",
    },
    paragraphFontSize: {
      type: String,
      default: "",
    },
    headerFontSize: {
      type: String,
      default: "",
    },
    headlineText: {
      type: String,
      default: "",
    },
    paragraphText: {
      type: String,
      default: "",
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "fontFamilyChanged",
    "boldnessChanged",
    "fontSizeChanged",
    "paragraphFontSizeChanged",
    "headerFontSizeChanged",
    "textColorChanged",
    "backgroundColorChanged",
    "headlineTextChanged",
    "paragraphTextChanged",
    "toggleVisibilityHeader",
  ],
  setup(props, { emit }) {
    const editor = useEditor({
      extensions: [
        StarterKit,
        TextStyle,
        FontFamily,
        FontSize,
        TextColor,
        BackgroundColor,
        Boldness,
      ],
    });

    const fontSizes = [
      "10",
      "11",
      "12",
      "13",
      "14",
      "15",
      "16",
      "17",
      "18",
      "19",
      "20",
      "21",
      "22",
      "23",
      "24",
      "25",
      "26",
      "27",
      "28",
      "29",
      "30",
    ];

    // Use the composable for font and color customization
    const {
      fonts,
      fontWeights,
      selectedFontFamily,
      selectedBoldness,
      applyFontFamily,
      applyFontSize,
      applyBoldness,
      applyTextColor,
      alertUser,
      beforeEnter,
      enter,
      leave,
    } = useStylingSettings(
      editor,
      emit as (event: string, ...args: any[]) => void,
      {
        fontFamily: props.fontFamily,
        boldness: props.boldness,
      }
    );

    // These are specific to this component and different from the composable
    const textColor = ref(props.headerTextColor);
    const selectedBackgroundColor = ref(props.backgroundColor);
    const localHeadlineText = ref(props.headlineText);
    const localParagraphText = ref(props.paragraphText);
    const headlineTextWarning = ref(false);
    const paragraphTextWarning = ref(false);
    const isCollapsed = ref(true);
    const selectedParagraphFontSize = ref(props.paragraphFontSize || "");
    const selectedHeaderFontSize = ref(props.headerFontSize || "");
    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
    };

    const applyBackgroundColor = (event: Event) => {
      const target = event.target as HTMLInputElement;
      selectedBackgroundColor.value = target.value;
      emit("backgroundColorChanged", selectedBackgroundColor.value);
      editor.value
        ?.chain()
        .focus()
        .setMark("backgroundColor", {
          backgroundColor: selectedBackgroundColor.value,
        })
        .run();
    };

    const handleHeadlineTextChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.value.length > 50) {
        alertUser(headlineTextWarning);
      }
      localHeadlineText.value = target.value.slice(0, 50);
      emit("headlineTextChanged", localHeadlineText.value);
    };

    const handleParagraphTextChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.value.length > 100) {
        alertUser(paragraphTextWarning);
      }
      localParagraphText.value = target.value.slice(0, 100);
      emit("paragraphTextChanged", localParagraphText.value);
    };

    const toggleVisibilityHeader = () => {
      emit("toggleVisibilityHeader", !props.isVisible);
    };

    return {
      editor,
      applyFontFamily,
      applyFontSize,
      applyTextColor,
      applyBoldness,
      fonts,
      fontSizes,
      fontWeights,
      textColor,
      selectedFontFamily,
      selectedBoldness,
      ARROW_DOWN,
      ARROW_UP,
      isCollapsed,
      toggleCollapse,
      beforeEnter,
      enter,
      leave,
      handleHeadlineTextChange,
      localHeadlineText,
      headlineTextWarning,
      paragraphTextWarning,
      localParagraphText,
      handleParagraphTextChange,
      selectedBackgroundColor,
      applyBackgroundColor,
      selectedParagraphFontSize,
      selectedHeaderFontSize,
      toggleVisibilityHeader,
    };
  },
});
</script>
